import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const ROLES = [
  'Manager / Associate Manager',
  'Head of Department',
  'Lead',
  'Sr. Marketing Artist',
  'Marketing Artist',
  'Sub-Lead',
  'Jr. Marketing Artist'
];

export const PERFORMANCE_PARAMS = [
  { 
    key: 'quality_consistency', 
    label: 'Design Quality & Consistency', 
    tooltip: 'Measures visual quality, design thinking, and consistency of output across tasks.',
    whyItMatters: 'Design quality is the foundation of creative work. High quality execution ensures that the team maintains strong visual standards and delivers impactful results. Consistent quality builds trust, reliability, and creative credibility.',
    scale: [
      'Significant improvement required',
      'Below expectations',
      'Meets expectations',
      'Strong performance',
      'Exceptional performance'
    ],
    descriptions: [
      'Design output frequently shows weak visual structure or poor execution.',
      'Some design effort is visible, but output often lacks polish or consistency.',
      'Design quality meets acceptable standards and work is generally usable.',
      'Design output is visually strong and consistently well executed.',
      'Design output is outstanding and consistently raises the creative standard of the team.'
    ],
    reasonExamples: [
      'The work required major corrections due to weak layout structure and inconsistent design execution.',
      'The work showed some effort but lacked refinement and required several corrections before final approval.',
      'The designs met the required standard, though further improvement in finishing and consistency would strengthen the output.',
      'The designer delivered polished and visually consistent work across tasks with minimal corrections.',
      'The designer delivered exceptional work quality throughout the review period, demonstrating strong creative thinking and excellent execution.'
    ]
  },
  { 
    key: 'brief_understanding', 
    label: 'Brief Understanding', 
    tooltip: 'Measures how accurately the designer interprets and executes the design brief.',
    whyItMatters: 'Correct understanding of the brief reduces revisions and ensures that the design solves the intended problem. Strong brief understanding improves efficiency, communication, and creative alignment.',
    scale: [
      'Significant improvement required',
      'Below expectations',
      'Meets expectations',
      'Strong performance',
      'Exceptional performance'
    ],
    descriptions: [
      'Frequent misunderstanding of task requirements.',
      'Partial understanding but important instructions are missed.',
      'General understanding of the brief with minor clarification needed.',
      'Clear understanding of requirements with minimal clarification needed.',
      'Excellent interpretation of the brief and thoughtful execution.'
    ],
    reasonExamples: [
      'The design output missed important elements of the brief, resulting in incorrect execution.',
      'Some key requirements from the brief were not captured properly, causing additional revisions.',
      'The designer understood the main requirements but required some guidance to refine the output.',
      'The designer demonstrated strong understanding of the brief and translated the requirements into effective visual output.',
      'The designer showed exceptional clarity in understanding the project objectives and delivered highly relevant solutions.'
    ]
  },
  { 
    key: 'speed_vs_accuracy', 
    label: 'Efficiency (Speed with Accuracy)', 
    tooltip: 'Measures how effectively the designer balances delivery speed with accuracy.',
    whyItMatters: 'Efficiency ensures that deadlines are met while maintaining quality. Strong efficiency improves team productivity and workflow stability.',
    scale: [
      'Significant improvement required',
      'Below expectations',
      'Meets expectations',
      'Strong performance',
      'Exceptional performance'
    ],
    descriptions: [
      'Delivery is consistently slow or inaccurate.',
      'Inconsistent balance between speed and accuracy.',
      'Expected turnaround time with acceptable accuracy.',
      'Efficient task completion with strong accuracy.',
      'Outstanding productivity and dependable delivery.'
    ],
    reasonExamples: [
      'Tasks required repeated follow-up due to delays and avoidable mistakes.',
      'Some tasks were completed on time, but overall workflow efficiency remained inconsistent.',
      'Tasks were delivered within reasonable timelines and met acceptable accuracy standards.',
      'The designer handled tasks efficiently while maintaining strong attention to detail.',
      'The designer consistently delivered work quickly without compromising quality or accuracy.'
    ]
  },
  { 
    key: 'guideline_adherence', 
    label: 'Guideline Adherence', 
    tooltip: 'Measures how well design standards, brand guidelines, and templates are followed.',
    whyItMatters: 'Following guidelines ensures that all outputs remain consistent, professional, and aligned with brand standards. Strong guideline adherence prevents rework and maintains production efficiency.',
    scale: [
      'Significant improvement required',
      'Below expectations',
      'Meets expectations',
      'Strong performance',
      'Exceptional performance'
    ],
    descriptions: [
      'Guidelines frequently ignored or missed.',
      'Guidelines partially followed but several issues observed.',
      'Guidelines followed at an acceptable level.',
      'Strong adherence to brand and production guidelines.',
      'Excellent discipline in maintaining standards.'
    ],
    reasonExamples: [
      'Several outputs did not follow required standards, causing inconsistencies.',
      'Some guideline elements were applied incorrectly or missed in multiple tasks.',
      'Most design standards were applied correctly, with only minor corrections needed.',
      'The designer followed design standards consistently, producing reliable outputs.',
      'The designer demonstrated exceptional consistency in applying guidelines across all deliverables.'
    ]
  },
  { 
    key: 'team_discipline', 
    label: 'Professional Behaviour & Team Discipline', 
    tooltip: 'Measures reliability, teamwork, communication, and responsibility.',
    whyItMatters: 'Creative teams succeed when members maintain professional conduct, accountability, and collaboration. Strong professional behaviour improves team morale and project execution.',
    scale: [
      'Significant improvement required',
      'Below expectations',
      'Meets expectations',
      'Strong performance',
      'Exceptional performance'
    ],
    descriptions: [
      'Behaviour issues negatively impact workflow.',
      'Some concerns regarding reliability or communication.',
      'Acceptable professional conduct.',
      'Reliable and positive contributor to the team.',
      'Exceptional professionalism and leadership attitude.'
    ],
    reasonExamples: [
      'Repeated discipline or communication issues affected team coordination.',
      'Inconsistency in communication and responsiveness impacted workflow during the review period.',
      'The designer maintained expected behaviour and fulfilled team responsibilities appropriately.',
      'The designer showed strong professionalism and collaborated well with teammates.',
      'The designer demonstrated outstanding responsibility and contributed positively to the team environment.'
    ]
  },
];

export const ERROR_CONTROL_PARAM = {
  key: 'error_control',
  label: 'Error Control / Revision Discipline',
  tooltip: 'Measures accuracy of work and how often revisions are required.',
  whyItMatters: 'Reducing avoidable errors saves time and improves overall team efficiency.',
  isOptional: true,
  scale: [
    'Frequent repeated mistakes',
    'Multiple avoidable corrections',
    'Moderate revision level',
    'Minimal corrections required',
    'Work often approved with very few revisions'
  ],
  descriptions: [
    'Work frequently contains repeated mistakes requiring multiple correction cycles.',
    'Multiple avoidable corrections needed before work can be approved.',
    'Moderate revision level - some corrections needed but within acceptable range.',
    'Minimal corrections required - work is mostly accurate on first submission.',
    'Work is often approved with very few or no revisions needed.'
  ],
  reasonExamples: [
    'Tasks required repeated corrections due to recurring errors in execution.',
    'Several avoidable mistakes were observed, causing additional revision cycles.',
    'Work required a moderate number of revisions before final approval.',
    'The designer submitted accurate work with only minor adjustments needed.',
    'The designer consistently delivered accurate work that was approved quickly.'
  ]
};

// Parameter weights as per Performance Policy (total = 100%)
export const PARAM_WEIGHTS = {
  quality_consistency: 0.30,    // 30%
  brief_understanding: 0.20,    // 20%
  speed_vs_accuracy: 0.15,      // 15% (Efficiency)
  error_control: 0.15,          // 15%
  guideline_adherence: 0.10,    // 10%
  team_discipline: 0.10,        // 10% (Professional Behaviour)
};

// Performance levels based on 0-100 scale
export const PERFORMANCE_LEVELS = [
  { min: 90, max: 100, label: 'Outstanding', color: 'emerald' },
  { min: 80, max: 89, label: 'Strong Performer', color: 'blue' },
  { min: 70, max: 79, label: 'Reliable Performer', color: 'sky' },
  { min: 60, max: 69, label: 'Needs Improvement', color: 'amber' },
  { min: 0, max: 59, label: 'Performance Concern', color: 'red' },
];

export const getPerformanceLevel = (score) => {
  for (const level of PERFORMANCE_LEVELS) {
    if (score >= level.min && score <= level.max) {
      return level;
    }
  }
  return PERFORMANCE_LEVELS[PERFORMANCE_LEVELS.length - 1];
};

// Calculate Performance Score (0-100) from individual parameter scores (1-5)
export const calculatePerformanceScore = (scores) => {
  const weights = PARAM_WEIGHTS;
  let totalWeight = 0;
  let weightedSum = 0;
  
  Object.keys(weights).forEach(key => {
    const score = scores[key] || 0;
    if (score > 0) {
      // Convert 1-5 scale to 0-100: ((score - 1) / 4) * 100
      const normalizedScore = ((score - 1) / 4) * 100;
      weightedSum += normalizedScore * weights[key];
      totalWeight += weights[key];
    }
  });
  
  // If not all parameters scored, normalize by actual weight used
  return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;
};

// Calculate Ranking Score: 80% Performance + 20% Contribution
export const calculateRankingScore = (performanceScore, contributionScore) => {
  return Math.round((performanceScore * 0.8) + (contributionScore * 0.2));
};

export const getScoreColor = (score) => {
  if (score >= 4.5) return 'text-emerald-600';
  if (score >= 4) return 'text-blue-600';
  if (score >= 3.5) return 'text-amber-600';
  return 'text-red-600';
};

export const getScoreBg = (score) => {
  if (score >= 4.5) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  if (score >= 4) return 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  if (score >= 3.5) return 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  return 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300';
};

// For 0-100 scale scores
export const getPerformanceScoreColor = (score) => {
  if (score >= 90) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 80) return 'text-blue-600 dark:text-blue-400';
  if (score >= 70) return 'text-sky-600 dark:text-sky-400';
  if (score >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
};

export const getPerformanceScoreBg = (score) => {
  if (score >= 90) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  if (score >= 80) return 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  if (score >= 70) return 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300';
  if (score >= 60) return 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  return 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300';
};

export const getPerformanceLabelColor = (label) => {
  if (label.includes('Outstanding')) return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700';
  if (label.includes('Strong Performer')) return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700';
  if (label.includes('Reliable Performer')) return 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/40 dark:text-sky-300 dark:border-sky-700';
  if (label.includes('Needs Improvement')) return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700';
  if (label.includes('High Performer')) return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700';
  if (label.includes('On Track')) return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700';
  return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700';
};

export const getRiskLabelColor = (label) => {
  if (label === 'Low Risk') return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700';
  if (label === 'Medium Risk') return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700';
  return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700';
};

export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const getCurrentWeek = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const dayOfMonth = now.getDate();
  return Math.ceil(dayOfMonth / 7);
};
